/**
 * <SEO /> — declarative meta tags per route.
 * Uses react-helmet-async under HelmetProvider (see App.js).
 */
import React from 'react';
import { Helmet } from 'react-helmet-async';

const SITE_URL = 'https://archetype.thereforedesign.co.in';

export default function SEO({
  title = "Brand Archetype Quiz for Founders | Discover Your Brand's Personality | Therefore",
  description = "India's first Brand Archetype Quiz for founders. Discover your brand's personality in 10 minutes based on Carl Jung's 12 archetypes. Free report, no signup.",
  path = '/',
  image = '/og-image.png',
  keywords,
  noindex = false,
  jsonLd,
}) {
  const url = `${SITE_URL}${path}`;
  const imageUrl = image?.startsWith('http') ? image : `${SITE_URL}${image}`;

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={url} />
      {noindex && <meta name="robots" content="noindex,nofollow" />}

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:site_name" content="Therefore Brand Archetype Quiz" />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={imageUrl} />

      {/* JSON-LD (array or single) */}
      {jsonLd && (Array.isArray(jsonLd) ? jsonLd : [jsonLd]).map((obj, i) => (
        <script key={`ld-${i}`} type="application/ld+json">
          {JSON.stringify(obj)}
        </script>
      ))}
    </Helmet>
  );
}

/* ─────────────────── Schema builders ─────────────────── */

export const orgSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'Therefore Design',
  url: 'https://thereforedesign.co.in',
  logo: `${SITE_URL}/logo.png`,
  sameAs: ['https://thereforedesign.co.in'],
});

export const websiteSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: 'Therefore Brand Archetype Quiz',
  url: SITE_URL,
  potentialAction: {
    '@type': 'SearchAction',
    target: `${SITE_URL}/?q={search_term_string}`,
    'query-input': 'required name=search_term_string',
  },
});

export const productSchema = ({ ratingValue, ratingCount } = {}) => {
  const s = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: 'Brand Archetype Quiz — Brand Personality Blueprint',
    description: "A Jungian brand archetype assessment for founders. Discover your brand's dominant personality in 10 minutes and receive a strategic blueprint covering visual identity and tone of voice.",
    brand: { '@type': 'Brand', name: 'Therefore Design' },
    image: `${SITE_URL}/og-image.png`,
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'INR',
      availability: 'https://schema.org/InStock',
      url: SITE_URL,
    },
  };
  if (ratingValue && ratingCount >= 3) {
    s.aggregateRating = {
      '@type': 'AggregateRating',
      ratingValue: String(ratingValue),
      reviewCount: String(ratingCount),
    };
  }
  return s;
};

export const howToSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'HowTo',
  name: "How to discover your brand's archetype",
  description: "A four-step process to identify your brand's dominant Jungian personality archetype.",
  totalTime: 'PT10M',
  step: [
    { '@type': 'HowToStep', position: 1, name: 'Answer 30 questions', text: 'About how you think, decide, and lead your brand.' },
    { '@type': 'HowToStep', position: 2, name: 'Pick visual preferences', text: 'Imagery and aesthetics that feel right for your brand.' },
    { '@type': 'HowToStep', position: 3, name: 'Get your archetype', text: 'Plus a scorecard across all 12 Jungian archetypes.' },
    { '@type': 'HowToStep', position: 4, name: 'Download the premium report', text: 'A 16-page brand blueprint covering visual identity and tone of voice.' },
  ],
});

export const faqSchema = (items = []) => {
  if (!items.length) return null;
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items.map(({ question, answer }) => ({
      '@type': 'Question',
      name: question,
      acceptedAnswer: { '@type': 'Answer', text: answer },
    })),
  };
};

export const breadcrumbSchema = (items = []) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((it, i) => ({
    '@type': 'ListItem',
    position: i + 1,
    name: it.name,
    item: `${SITE_URL}${it.path}`,
  })),
});
